import { QueryErrorBanner } from "../components/QueryErrorBanner";
import {
  Badge,
  Banner,
  BlockStack,
  Box,
  Button,
  Card,
  InlineGrid,
  InlineStack,
  Layout,
  Modal,
  Page,
  SkeletonBodyText,
  Text,
} from "@shopify/polaris";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { getBillingContext, listPlans, subscribe } from "../api/billing";
import { getMe } from "../api/me";
import { t } from "../lib/i18n";
import { applyAuthoritativeSubscription } from "../lib/planAuthority";
import { BillingSurveyGateModal } from "../components/BillingSurveyGateModal";
import type { BillingPlan } from "../types";
import { money } from "../utils/money";
import { redirectTopLevel } from "../utils/redirect";

// 各プランの機能一覧は backend `entitlements.py` の FEATURE_MIN_TIER と対応させる
// （2026-07-12 再バランス／2026-07-13 通知階層見直し）: Free=Stocky移行＋手動注文書
// のウェッジ、Starter=発注自動化（発注点管理＋メール発注リマインダー＋自動注文書）、
// Pro=在庫分析＋30日履歴＋LINE・Slack 通知、Business=拠点数 無制限。発注リマインダー
// 🔴 拠点数は「階段」（Free 1 / Starter 2 / Pro 5 / Business 無制限）＝真実源は
// backend `entitlements.PLAN_LOCATION_LIMITS` と LP `pricing.ts` の locationLimit。
// 旧「拠点別＝Business」の表記は 2026-08-01 の階段化で失効している＝戻さない
// （特商法ページは階段を掲載済みで、ここだけ古いと課金判断の画面が嘘をつく）。
// （アラート）は発注点が前提のため Starter+。メール通知は Starter ベースライン、
// 高開封の LINE と Slack は Pro レバー。Free には発注アラートは付かない。
// productLimit は Shopify の active 商品数の上限（プラン別）。真実源は backend
// `entitlements.PLAN_PRODUCT_LIMITS`（Free 100 / Starter 1,000 / Pro 10,000 /
// Business 無制限）＝ここはその表示ミラー。値を変えるときは backend と一緒に直す。
const PLAN_COPY: Record<
  string,
  { tagline: string; productLimit: string; features: string[] }
> = {
  free: {
    tagline: "Stocky からの移行と手動運用に",
    productLimit: "商品数 100 件まで",
    features: [
      "Shopify 在庫の同期",
      "StockyデータのCSV・貼り付け移行",
      "手動で注文書を作成（数量手入力・PDF/CSV）",
      "売れ筋 TOP 10・在庫の見える化",
      "拠点数 1",
    ],
  },
  starter: {
    tagline: "発注の自動化を始めたい方へ",
    productLimit: "商品数 1,000 件まで",
    features: [
      "Free のすべて",
      "発注点の自動計算（発注点管理）",
      "メールで発注リマインダー",
      "発注数量を自動提案（自動注文書）",
      "拠点数 2 まで",
    ],
  },
  pro: {
    tagline: "在庫を数字で管理したい",
    productLimit: "商品数 10,000 件まで",
    features: [
      "Starter のすべて",
      "在庫分析（評価額・ABC分析）",
      "30 日売上履歴",
      "LINE・Slack 通知",
      "拠点数 5 まで",
    ],
  },
  business: {
    tagline: "大規模・多拠点の運用に",
    productLimit: "商品数 無制限",
    features: [
      "Pro のすべて",
      "拠点数 無制限",
      "電話サポート",
    ],
  },
};

const INVENTORY_ANALYTICS_PATH = "/inventory-analytics";
const DATA_IMPORT_PATH = "/stocky-csv";

export function Plans() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const subscribeRequestInFlightRef = useRef(false);
  const meQuery = useQuery({ queryKey: ["me"], queryFn: getMe });
  const plansQuery = useQuery({ queryKey: ["plans"], queryFn: listPlans });
  const billingContextQuery = useQuery({
    queryKey: ["billing-context"],
    queryFn: getBillingContext,
  });

  const subscribeMutation = useMutation({
    mutationFn: async ({
      planCode,
      continueToImport = false,
    }: {
      planCode: string;
      continueToImport?: boolean;
    }) => {
      const expectedShopId = meQuery.data?.shop_id;
      const data = await subscribe(planCode);
      if (!continueToImport) return data;

      // The shortcut is Free-only. Do not navigate from a response that would
      // create a Shopify approval page, belongs to another shop, or has not
      // become ACTIVE. Then confirm the same state through fresh /api/me data.
      if (
        !expectedShopId ||
        data.shop_id !== expectedShopId ||
        data.plan_code !== "free" ||
        data.confirmation_url !== null ||
        data.subscription_status.toLowerCase() !== "active"
      ) {
        throw new Error(
          t("Free プランの有効化を確認できませんでした。再試行してください。"),
        );
      }
      await queryClient.invalidateQueries({
        queryKey: ["me"],
        exact: true,
        refetchType: "none",
      });
      const authoritativeMe = await queryClient.fetchQuery({
        queryKey: ["me"],
        queryFn: getMe,
        staleTime: 0,
      });
      if (
        authoritativeMe.shop_id !== expectedShopId ||
        authoritativeMe.subscription.active !== true ||
        authoritativeMe.subscription.plan_code !== "free" ||
        authoritativeMe.subscription.status !== "active"
      ) {
        throw new Error(
          t("Free プランの有効化を確認できませんでした。再試行してください。"),
        );
      }
      return data;
    },
    onSuccess: async (data, variables) => {
      // Paid plan: Shopify returns a hosted approval page. Redirect the TOP
      // window out of the iframe; entitlements refresh on return via /billing/sync.
      if (data.confirmation_url) {
        redirectTopLevel(data.confirmation_url);
        return;
      }
      if (variables.continueToImport) {
        navigate(DATA_IMPORT_PATH);
        return;
      }
      // Free plan: no Shopify approval page. The backend already finalized the
      // authoritative local state (and, for a downgrade, cancelled paid billing),
      // so publish that result before any stale paid surface can render. A fresh
      // /api/me row then confirms every remaining display field.
      const active = data.subscription_status === "active";
      applyAuthoritativeSubscription(queryClient, {
        shop_id: data.shop_id,
        active,
        plan_code: active ? data.plan_code : null,
        status: active ? "active" : null,
      });
      await queryClient.invalidateQueries({
        queryKey: ["me"],
        exact: true,
        refetchType: "all",
      });
    },
  });

  // Return flow ( ?billing_pending=1&charge_id=...) is handled at App level in
  // useBillingSync() — it lands on Home, not Plans, after the Shopify redirect.

  // 課金直前の飛ばせないアンケート関門。未回答の店が有料プランを選んだら、実際の
  // subscribe 呼び出しはアンケート送信後まで保留する（BillingSurveyGateModal 参照）。
  // Free プランは対象外＝無料のまま使い続ける店に必須回答を強制しない（決定）。
  const [pendingPlanCode, setPendingPlanCode] = useState<string | null>(null);
  const [confirmFreeDowngrade, setConfirmFreeDowngrade] = useState(false);
  const [pendingPaidChangePlanCode, setPendingPaidChangePlanCode] = useState<
    string | null
  >(null);
  // 新SPA→旧backendの rollout skew は fail-closed。me 未取得中は既存 loading、
  // 取得後は false の明示がある場合だけ plan action を解放する。
  const referralProbeRequired =
    meQuery.data !== undefined && meQuery.data.needs_referral_probe !== false;

  function handleSelectPlan(planCode: string) {
    if (
      referralProbeRequired ||
      subscribeRequestInFlightRef.current ||
      subscribeMutation.isPending ||
      pendingPlanCode !== null ||
      pendingPaidChangePlanCode !== null
    ) {
      return;
    }
    if (
      planCode === "free" &&
      isActive &&
      currentPlanCode !== null &&
      currentPlanCode !== "free"
    ) {
      setConfirmFreeDowngrade(true);
      return;
    }
    if (
      planCode !== "free" &&
      isActive &&
      currentPlanCode !== null &&
      currentPlanCode !== "free" &&
      currentPlanCode !== planCode
    ) {
      setPendingPaidChangePlanCode(planCode);
      return;
    }
    if (planCode !== "free" && meQuery.data?.needs_survey) {
      setPendingPlanCode(planCode);
      return;
    }
    startSubscription(planCode);
  }

  function startSubscription(planCode: string, continueToImport = false) {
    if (
      referralProbeRequired ||
      subscribeRequestInFlightRef.current ||
      subscribeMutation.isPending
    ) {
      return;
    }
    subscribeRequestInFlightRef.current = true;
    subscribeMutation.mutate(
      { planCode, continueToImport },
      {
        onSettled: () => {
          subscribeRequestInFlightRef.current = false;
        },
      },
    );
  }

  function handleSurveyAnswered() {
    if (referralProbeRequired || subscribeMutation.isPending) return;
    const planCode = pendingPlanCode;
    setPendingPlanCode(null);
    if (planCode) {
      startSubscription(planCode);
    }
  }

  function confirmPaidPlanChange() {
    if (
      referralProbeRequired ||
      subscribeRequestInFlightRef.current ||
      subscribeMutation.isPending
    ) {
      return;
    }
    const planCode = pendingPaidChangePlanCode;
    if (!planCode) return;
    setPendingPaidChangePlanCode(null);
    if (meQuery.data?.needs_survey) {
      setPendingPlanCode(planCode);
      return;
    }
    startSubscription(planCode);
  }

  const currentPlanCode = meQuery.data?.subscription.plan_code ?? null;
  const isActive = meQuery.data?.subscription.active ?? false;
  const currentPlanName =
    (plansQuery.data ?? []).find((plan) => plan.code === currentPlanCode)?.name ??
    currentPlanCode ??
    t("不明");
  const pendingPaidChangePlanName =
    (plansQuery.data ?? []).find(
      (plan) => plan.code === pendingPaidChangePlanCode,
    )?.name ??
    pendingPaidChangePlanCode ??
    t("不明");

  // `/api/me` が確定するまではカードを出さない。Shopify 実請求通貨だけが
  // 取れない場合はカードと Free 解約導線を残し、有料プランの作成だけを
  // fail-closed にする。
  const hasSubscriptionContext =
    meQuery.data !== undefined &&
    (!meQuery.data.subscription.active ||
      (typeof meQuery.data.subscription.plan_code === "string" &&
        meQuery.data.subscription.plan_code !== ""));
  const billingCurrency =
    billingContextQuery.data?.currency === "JPY" ||
    billingContextQuery.data?.currency === "USD"
      ? billingContextQuery.data.currency
      : null;
  // カタログ価格はストア通貨に合わせる。開発ストアでは Shopify のアプリ請求
  // 通貨が USD のままでも、JPY ストアには日本向けの円価格を表示する。
  // 実際の mutation を許可するかは上の billingCurrency で別途判定する。
  const displayCurrency = meQuery.data?.currency === "JPY" ? "JPY" : "USD";
  const hasChargeCurrency = billingCurrency !== null;
  const currentPlanIsKnown =
    !isActive ||
    (currentPlanCode !== null &&
      (plansQuery.data ?? []).some((plan) => plan.code === currentPlanCode));
  const subscriptionContextFailed =
    meQuery.isError || (meQuery.isSuccess && !hasSubscriptionContext);
  const billingCurrencyFailed =
    billingContextQuery.isError ||
    (billingContextQuery.isSuccess && !hasChargeCurrency);
  const currentPlanMismatch =
    plansQuery.isSuccess &&
    meQuery.isSuccess &&
    isActive &&
    !currentPlanIsKnown;
  const canShowPlanCatalog =
    plansQuery.isSuccess &&
    meQuery.isSuccess &&
    hasSubscriptionContext &&
    currentPlanIsKnown;
  const planActionsLocked =
    referralProbeRequired ||
    subscribeMutation.isPending ||
    pendingPlanCode !== null ||
    confirmFreeDowngrade ||
    pendingPaidChangePlanCode !== null;

  const showUsdPrice = displayCurrency === "USD";

  // オーナーが手で配る無料期間。available のときだけ日数を出す＝
  // 消化済みの店に「無料になります」と出し続けない。
  const coupon = meQuery.data?.launch_coupon;
  const couponDays = coupon?.available ? coupon.trial_days : null;

  // トライアル窓（店単位で1回きり）。null = 未使用＝プラン既定が丸ごと使える／
  // 0 = 使い切り＝次の購読は即課金。窓が開いた店に「14日間無料」を出し続けると
  // Shopify の承認画面（実効値）と食い違う＝ここで実効値に揃える。
  const trialRemainingDays = meQuery.data?.trial?.remaining_days ?? null;

  return (
    <Page title={t("プラン")}>
      <BillingSurveyGateModal
        open={pendingPlanCode !== null}
        onAnswered={handleSurveyAnswered}
      />
      <Modal
        open={confirmFreeDowngrade}
        onClose={() => setConfirmFreeDowngrade(false)}
        title={t("Free プランへ変更しますか？")}
        primaryAction={{
          content: t("有料プランを解約して Free へ変更"),
          destructive: true,
          loading: subscribeMutation.isPending,
          disabled: referralProbeRequired || subscribeMutation.isPending,
          onAction: () => {
            if (referralProbeRequired || subscribeMutation.isPending) return;
            setConfirmFreeDowngrade(false);
            startSubscription("free");
          },
        }}
        secondaryActions={[
          {
            content: t("変更しない"),
            onAction: () => setConfirmFreeDowngrade(false),
          },
        ]}
      >
        <Modal.Section>
          <BlockStack gap="200">
            <Text as="p">
              {t(
                "確定すると Shopify の現在の有料契約をすぐに解約し、Free プランへ変更します。",
              )}
            </Text>
            <Text as="p" tone="critical">
              {t(
                "発注点の自動計算、発注リマインダー、在庫分析など有料プランの機能は利用できなくなります。",
              )}
            </Text>
            <Text as="p" variant="bodySm" tone="subdued">
              {t(
                "保存済みのデータは削除されません。再契約すると再び利用できます。",
              )}
            </Text>
          </BlockStack>
        </Modal.Section>
      </Modal>
      <Modal
        open={pendingPaidChangePlanCode !== null}
        onClose={() => setPendingPaidChangePlanCode(null)}
        title={t("有料プランの変更を確認")}
        primaryAction={{
          content: t("{plan}へ変更する", {
            plan: pendingPaidChangePlanName,
          }),
          loading: subscribeMutation.isPending,
          disabled:
            referralProbeRequired ||
            subscribeRequestInFlightRef.current ||
            subscribeMutation.isPending,
          onAction: confirmPaidPlanChange,
        }}
        secondaryActions={[
          {
            content: t("キャンセル"),
            onAction: () => setPendingPaidChangePlanCode(null),
          },
        ]}
      >
        <Modal.Section>
          <BlockStack gap="200">
            <Text as="p">
              {t("現在のプラン: {plan}", { plan: currentPlanName })}
            </Text>
            <Text as="p">
              {t("変更先のプラン: {plan}", {
                plan: pendingPaidChangePlanName,
              })}
            </Text>
            <Text as="p" variant="bodySm" tone="subdued">
              {t(
                "確認後にShopifyの承認画面へ進みます。承認するまでプランは変更されません。",
              )}
            </Text>
          </BlockStack>
        </Modal.Section>
      </Modal>
      <Layout>
        {meQuery.data && hasSubscriptionContext && !isActive ? (
          <Layout.Section>
            <Banner
              tone="info"
              title={t("Stockyデータ取込はFreeプランで利用できます")}
            >
              <BlockStack gap="300">
                <Text as="p">
                  {t(
                    "FreeプランではShopifyへの請求は発生しません。まず保存済みStockyデータを取り込み、必要になったときだけ有料プランを比較できます。",
                  )}
                </Text>
                <InlineStack>
                  <Button
                    variant="primary"
                    loading={
                      subscribeMutation.isPending &&
                      subscribeMutation.variables?.continueToImport === true
                    }
                    disabled={
                      referralProbeRequired ||
                      subscribeMutation.isPending ||
                      pendingPlanCode !== null ||
                      pendingPaidChangePlanCode !== null ||
                      confirmFreeDowngrade
                    }
                    onClick={() => {
                      startSubscription("free", true);
                    }}
                  >
                    {t("Freeでデータ取込を始める")}
                  </Button>
                </InlineStack>
              </BlockStack>
            </Banner>
          </Layout.Section>
        ) : null}
        {couponDays ? (
          <Layout.Section>
            <Banner tone="success" title={t("無料期間特典が適用されています")}>
              <p>
                {t(
                  t(
                    "Free 以外のプランを選ぶと、最初の {days} 日間（約 {months} ヶ月）が無料になります。無料期間は Shopify の承認画面にも表示されます。期間が終わると通常料金へ移行します（それまでにいつでも解約できます）。",
                  ),
                  { days: couponDays, months: Math.round(couponDays / 30) },
                )}
              </p>
            </Banner>
          </Layout.Section>
        ) : null}

        <Layout.Section>
          <Card>
            <BlockStack gap="200">
              <Text as="p" variant="bodyMd" tone="subdued">
                {t("いつでもアップグレード・ダウングレード・解約できます。")}
                {couponDays
                  ? t(
                      "Free 以外は最初の {days} 日間が無料です（無料期間特典）。",
                      {
                        days: couponDays,
                      },
                    )
                  : trialRemainingDays === null
                    ? t("Free 以外は 14 日間の無料トライアル付き。")
                    : trialRemainingDays > 0
                      ? t(
                          "標準の無料トライアルは1ストア通算14日です。現在の残りは {days} 日で、プラン変更でも引き継がれます。",
                          {
                            days: trialRemainingDays,
                          },
                        )
                      : t(
                          "無料トライアルは利用済みです。有料プランはすぐに課金が始まります。",
                        )}
              </Text>
              {showUsdPrice ? (
                <Text as="p" variant="bodySm" tone="subdued">
                  {t(
                    "有料プランは米ドル（USD）で請求されます（Shopify の請求通貨が日本円の場合は円建て）。正確な請求額は Shopify の承認画面でご確認いただけます。",
                  )}
                </Text>
              ) : null}
              <InlineStack>
                <Button
                  variant="plain"
                  onClick={() => navigate(INVENTORY_ANALYTICS_PATH)}
                >
                  {t("Pro の「在庫分析（評価額・ABC）」を見る")}
                </Button>
              </InlineStack>
            </BlockStack>
          </Card>
        </Layout.Section>

        {subscribeMutation.isError && (
          <Layout.Section>
            <Banner tone="critical">
              {t("プラン変更に失敗しました:")} {subscribeMutation.error.message}
            </Banner>
          </Layout.Section>
        )}

        {billingCurrencyFailed && canShowPlanCatalog ? (
          <Layout.Section>
            <QueryErrorBanner
              title={t("Shopify の請求通貨の取得に失敗しました")}
              onRetry={() => {
                void billingContextQuery.refetch();
              }}
            />
          </Layout.Section>
        ) : null}

        <Layout.Section>
          {plansQuery.isLoading || meQuery.isLoading ? (
            <Card>
              <SkeletonBodyText lines={6} />
            </Card>
          ) : canShowPlanCatalog && plansQuery.data ? (
            <InlineGrid gap="400" columns={{ xs: 1, sm: 2, md: 4 }}>
              {plansQuery.data.map((plan) => (
                <PlanCard
                  key={plan.code}
                  plan={plan}
                  trialDays={effectiveTrialDays(
                    plan.trial_days,
                    trialRemainingDays,
                    couponDays,
                  )}
                  trialDaysAreRemaining={
                    couponDays === null &&
                    trialRemainingDays !== null &&
                    trialRemainingDays > 0
                  }
                  displayCurrency={displayCurrency}
                  billingCurrency={billingCurrency}
                  isCurrent={isActive && currentPlanCode === plan.code}
                  loading={
                    (subscribeMutation.isPending &&
                      subscribeMutation.variables?.planCode === plan.code) ||
                    pendingPlanCode === plan.code
                  }
                  disabled={planActionsLocked}
                  onSelect={() => handleSelectPlan(plan.code)}
                />
              ))}
            </InlineGrid>
          ) : (
            <QueryErrorBanner
              title={
                subscriptionContextFailed || currentPlanMismatch
                  ? t("契約情報の取得に失敗しました")
                  : t("プランの取得に失敗しました")
              }
              onRetry={() => {
                void Promise.all([
                  plansQuery.refetch(),
                  meQuery.refetch(),
                  billingContextQuery.refetch(),
                ]);
              }}
            />
          )}
        </Layout.Section>
      </Layout>
    </Page>
  );
}

// バックエンド `launch_coupon.effective_trial_days` のミラー（表示専用＝実際に
// Shopify へ渡す日数はバックエンドが決める）。窓が開いた店は残り日数・未使用は
// プラン既定・未消化クーポンは max で効く（配ったせいで縮む逆転を起こさない）。
function effectiveTrialDays(
  planTrialDays: number,
  remainingDays: number | null,
  couponDays: number | null,
): number {
  const base = remainingDays ?? planTrialDays;
  return couponDays === null ? base : Math.max(couponDays, base);
}

function PlanCard({
  plan,
  trialDays,
  trialDaysAreRemaining,
  displayCurrency,
  billingCurrency,
  isCurrent,
  loading,
  disabled,
  onSelect,
}: {
  plan: BillingPlan;
  trialDays: number;
  trialDaysAreRemaining: boolean;
  displayCurrency: "JPY" | "USD";
  billingCurrency: "JPY" | "USD" | null;
  isCurrent: boolean;
  loading: boolean;
  disabled: boolean;
  onSelect: () => void;
}) {
  const copy = PLAN_COPY[plan.code] ?? {
    tagline: "",
    productLimit: "",
    features: [],
  };
  // JPY ストアは日本向けの円価格（¥2,980 / ¥5,980 / ¥12,800）、それ以外は
  // USD 価格（$19 / $39 / $83）を表示する。第一級カラム price_jpy_monthly /
  // price_usd_monthly を正とする（旧 feature_flags.price_jpy は 0021 で
  // カラム化後もドリフトし得るため使わない）。
  const priceJpy = plan.price_jpy_monthly;
  const isFree = priceJpy === 0;
  const priceLabel =
    displayCurrency === "USD"
      ? money(plan.price_usd_monthly, "USD")
      : `¥${priceJpy.toLocaleString("ja-JP")}`;
  return (
    <Card>
      <BlockStack gap="300">
        <InlineStack align="space-between" blockAlign="center">
          <Text as="h3" variant="headingMd">
            {plan.name}
          </Text>
          {isCurrent && <Badge tone="success">{t("利用中")}</Badge>}
        </InlineStack>
        <Text as="span" variant="bodySm" tone="subdued">
          {t(copy.tagline)}
        </Text>
        <Box paddingBlockStart="200" paddingBlockEnd="200">
          <Text as="span" variant="heading2xl">
            {isFree ? t("無料") : priceLabel}
          </Text>
          {!isFree && (
            <Text as="span" variant="bodySm" tone="subdued">
              {" "}
              {t("/ 月")}
            </Text>
          )}
        </Box>
        {copy.productLimit && (
          <Text as="span" variant="bodySm" fontWeight="semibold">
            {t(copy.productLimit)}
          </Text>
        )}
        <BlockStack gap="100">
          {copy.features.map((f) => (
            <Text key={f} as="span" variant="bodySm">
              {t("・")}
              {t(f)}
            </Text>
          ))}
        </BlockStack>
        <Box paddingBlockStart="300">
          <Button
            variant={isCurrent ? "secondary" : "primary"}
            onClick={onSelect}
            loading={loading}
            // Shopify billing currency is required only to create a paid
            // charge. Never make a temporary currency-read failure block a
            // merchant from cancelling an existing paid plan via Free.
            disabled={
              isCurrent || disabled || (!isFree && billingCurrency === null)
            }
            fullWidth
          >
            {isCurrent
              ? t("現在のプラン")
              : !isFree && trialDays > 0
                ? trialDaysAreRemaining
                  ? t("残り{days}日間", { days: trialDays })
                  : t("{days}日間無料で試す", { days: trialDays })
                : t("選ぶ")}
          </Button>
        </Box>
      </BlockStack>
    </Card>
  );
}
